import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, MutationCache } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { RBACProvider, ProtectedRoute } from './components/ui/RBAC';
import { Loader2 } from 'lucide-react';
import { BreadcrumbNameProvider } from './contexts/BreadcrumbNameContext';
import { useEffect } from 'react';

import AppLayout from './components/layouts/AppLayoutSecure';
import AuthLayout from './components/layouts/AuthLayoutSecure';

// Lazy loading dos componentes
const Login = lazy(() => import('./pages/auth/Login'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const ResetPasswordConfirm = lazy(() => import('./pages/auth/ResetPasswordConfirm'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const AssociadosLista = lazy(() => import('./pages/associados/AssociadosLista'));
const EmpresasLista = lazy(() => import('./pages/empresas/EmpresasLista'));
const FinanceiroVisaoGeral = lazy(() => import('./pages/financeiro/FinanceiroVisaoGeral'));

const ContasReceber = lazy(() => import('./pages/financeiro/ContasReceber'));
const ContasPagar = lazy(() => import('./pages/financeiro/ContasPagar'));
const Inadimplencia = lazy(() => import('./pages/financeiro/Inadimplencia'));
const FluxoCaixa = lazy(() => import('./pages/financeiro/FluxoCaixa'));
const ComunicacaoIndex = lazy(() => import('./pages/comunicacao/index'));
const Mensagens = lazy(() => import('./pages/comunicacao/Mensagens'));
const RelatoriosFinanceiros = lazy(() => import('./pages/financeiro/RelatoriosFinanceiros'));
const RelatoriosMensais = lazy(() => import('./pages/financeiro/RelatoriosMensais'));
const GerarCobrancasMensais = lazy(() => import('./pages/financeiro/GerarCobrancasMensais'));
const Administracao = lazy(() => import('./pages/administracao'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AssociadosEditar = lazy(() => import('./pages/associados/AssociadosEditar'));
const EmpresasEditar = lazy(() => import('./pages/empresas/EmpresasEditar'));
import UsuariosPage from './pages/administracao/usuarios-admin';
import LogsPage from './pages/administracao/logs';
import CadastroControle from './pages/administracao/controle';
const Configuracoes = lazy(() => import('./pages/administracao/configuracoes/index'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const DashboardPage = lazy(() => import('./modules/colonies/dashboard/DashboardPage'));
const ClubDetailsPage = lazy(() => import('./modules/colonies/club-details/ClubDetailsPage'));
const RelatoriosEmpresas = lazy(() => import('./pages/empresas/RelatoriosEmpresas'));
const RelatoriosAssociados = lazy(() => import('./pages/associados/RelatoriosAssociados'));

// Componente de Loading
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Aumentamos o staleTime para evitar refetchs desnecessários offline
      staleTime: 10 * 60 * 1000, // 10 minutos
      // Tempo de vida do cache no storage
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7 dias
      // Network mode 'always' garante que o cache seja usado mesmo em erro de rede
      networkMode: 'always',
    },
  },
  mutationCache: new MutationCache({
    onSettled: () => {
      // Persistir o estado das mutações quando algo mudar
      const state = queryClient.getMutationCache().getAll();
      set('react-query-mutations', state);
    },
  }),
});

// Persistência usando IndexedDB (idb-keyval)
const persister: any = {
  persistClient: async (client: any) => {
    try {
      await set('react-query-cache', client);
    } catch (error) {
      console.error('Erro ao persistir cache do React Query:', error);
    }
  },
  restoreClient: async () => {
    try {
      const cache = await get('react-query-cache');
      if (cache) {
        console.log('Cache do React Query restaurado com sucesso');
      }
      return cache;
    } catch (error) {
      console.error('Erro ao restaurar cache do React Query:', error);
      return undefined;
    }
  },
  removeClient: async () => {
    try {
      await del('react-query-cache');
    } catch (error) {
      console.error('Erro ao remover cache do React Query:', error);
    }
  },
};

const App = () => {
  useEffect(() => {
    // Restaurar mutações pausadas do storage ao iniciar
    const restoreMutations = async () => {
      try {
        const savedMutations = await get('react-query-mutations');
        if (savedMutations) {
          // Nota: React Query gerencia a restauração via hydrate se usarmos persistQueryClient
          // Mas para mutações pausadas, precisamos garantir que o client saiba retomá-las
          console.log('🔄 Restaurando mutações offline...', savedMutations);
        }
      } catch (error) {
        console.error('Erro ao restaurar mutações:', error);
      }
    };

    restoreMutations();

    // Listener para retomar mutações quando a rede voltar
    const handleOnline = () => {
      console.log('🌐 Conexão restaurada! Sincronizando dados...');
      queryClient.resumePausedMutations().then(() => {
        queryClient.invalidateQueries();
      });
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * 7, // 7 dias
        buster: 'v1', // Mudar isso para invalidar o cache se a estrutura mudar
      }}
    >
      <ThemeProvider>
        <AuthProvider>
          <RBACProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <HashRouter>
                <BreadcrumbNameProvider>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {/* Rotas de autenticação */}
                      <Route path="/login" element={<AuthLayout />}>
                        <Route index element={<Login />} />
                      </Route>
                      <Route path="/recuperar-senha" element={<AuthLayout />}>
                        <Route index element={<ResetPassword />} />
                      </Route>
                      <Route path="/reset-password-confirm" element={<AuthLayout />}>
                        <Route index element={<ResetPasswordConfirm />} />
                      </Route>
                      <Route path="/auth/verify" element={<AuthLayout />}>
                        <Route index element={<VerifyEmail />} />
                      </Route>

                      {/* Rotas protegidas */}
                      <Route path="/" element={<AppLayout />}>
                        <Route
                          index
                          element={
                            <ProtectedRoute
                              requiredRole={['presidencia', 'diretoria', 'financeiro', 'homologacao']}
                            >
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="dashboard"
                          element={
                            <ProtectedRoute
                              requiredRole={['presidencia', 'diretoria', 'financeiro', 'homologacao']}
                            >
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* Rotas de Administração */}
                        <Route path="administracao">
                          <Route
                            index
                            element={
                              <ProtectedRoute requiredRole={['presidencia', 'diretoria', 'juridico']}>
                                <Administracao />
                              </ProtectedRoute>
                            }
                          />
                          {/** Perfil agora é um modal global; rota removida */}
                          <Route
                            path="usuarios"
                            element={
                              <ProtectedRoute requiredRole={['presidencia', 'diretoria', 'juridico']}>
                                <UsuariosPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="configuracoes"
                            element={
                              <ProtectedRoute requiredRole={['presidencia', 'diretoria', 'juridico']}>
                                <Configuracoes />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="controle"
                            element={
                              <ProtectedRoute requiredRole={['presidencia', 'diretoria', 'juridico']}>
                                <CadastroControle />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="logs"
                            element={
                              <ProtectedRoute requiredRole={['presidencia', 'diretoria', 'juridico']}>
                                <LogsPage />
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Rotas de Associados */}
                        <Route path="associados">
                          <Route
                            index
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <AssociadosLista />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="consulta"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <AssociadosLista />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="editar/:id"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <AssociadosEditar />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="relatorios"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <RelatoriosAssociados />
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Rotas de Empresas */}
                        <Route path="empresas">
                          <Route
                            index
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <EmpresasLista />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="editar/:id"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <EmpresasEditar />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="relatorios"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <RelatoriosEmpresas />
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Rotas do Financeiro */}
                        <Route path="financeiro">
                          <Route
                            index
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <FinanceiroVisaoGeral />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="contas-receber"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <ContasReceber />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="contas-pagar"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <ContasPagar />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="inadimplencia"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <Inadimplencia />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="fluxo-caixa"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <FluxoCaixa />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="relatorios"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <RelatoriosFinanceiros />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="relatorios-mensais"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'financeiro']}
                              >
                                <RelatoriosMensais />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="gerar-cobrancas-mensais"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'financeiro']}
                              >
                                <GerarCobrancasMensais />
                              </ProtectedRoute>
                            }
                          />
                        </Route>



                        {/* Rotas de Comunicação */}
                        <Route path="comunicacao">
                          <Route
                            index
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <ComunicacaoIndex />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="mensagens"
                            element={
                              <ProtectedRoute
                                requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                              >
                                <Mensagens />
                              </ProtectedRoute>
                            }
                          />
                        </Route>

                        {/* Rotas do módulo de colônias */}
                        <Route
                          path="colonias/dashboard"
                          element={
                            <ProtectedRoute
                              requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                            >
                              <DashboardPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="colonias/clube/:clubId"
                          element={
                            <ProtectedRoute
                              requiredRole={['presidencia', 'diretoria', 'juridico', 'homologacao']}
                            >
                              <ClubDetailsPage />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Rota 404 */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BreadcrumbNameProvider>
              </HashRouter>
            </TooltipProvider>
          </RBACProvider>
        </AuthProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
};

export default App;
